import { ClientUser, ELDType } from '@numeo/types-core'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ViewType } from 'pages/dashboard/pages/updater-agent/pages/ongoing-loads/types'
import { RootState } from 'store'

interface DriverState {
    currentView: ViewType
    selectedDriver: ClientUser | null
    isLoading: boolean
    searchTerm: string
    selectedDriverIds: string[]
    importDialogOpen: boolean
    isImportDriverLoading: boolean
    terminateConfirmOpen: boolean
    importDriverModalOpen: boolean
    driversTrucksMatchingModalOpen: boolean
    truckAssignModalOpen: boolean
    pendingImportEldType: ELDType | null
    editInitialStep: number | null
    /** Pre-fill data from CDL parse, applied as initialData when opening the add driver wizard */
    cdlPrefillData: Omit<ClientUser, '_id'> | null
}

const initialState: DriverState = {
    currentView: 'list',
    selectedDriver: null,
    isLoading: false,
    searchTerm: '',
    selectedDriverIds: [],
    importDialogOpen: false,
    isImportDriverLoading: false,
    terminateConfirmOpen: false,
    importDriverModalOpen: false,
    driversTrucksMatchingModalOpen: false,
    truckAssignModalOpen: false,
    pendingImportEldType: null,
    editInitialStep: null,
    cdlPrefillData: null,
}

const driverSlice = createSlice({
    name: 'driver',
    initialState,
    reducers: {
        setCurrentView(state, action: PayloadAction<ViewType>) {
            state.currentView = action.payload
        },
        setSelectedDriver(state, action: PayloadAction<ClientUser | null>) {
            state.selectedDriver = action.payload
        },
        setIsLoading(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload
        },
        setSearchTerm(state, action: PayloadAction<string>) {
            state.searchTerm = action.payload
        },
        setSelectedDriverIds(state, action: PayloadAction<string[]>) {
            state.selectedDriverIds = action.payload
        },
        setImportDialogOpen(state, action: PayloadAction<boolean>) {
            state.importDialogOpen = action.payload
        },
        setTerminateConfirmOpen(state, action: PayloadAction<boolean>) {
            state.terminateConfirmOpen = action.payload
        },
        setTruckAssignModalOpen(state, action: PayloadAction<boolean>) {
            state.truckAssignModalOpen = action.payload
        },
        setIsImportDriverLoading(state, action: PayloadAction<boolean>) {
            state.isImportDriverLoading = action.payload
        },
        setPendingImportEldType(state, action: PayloadAction<ELDType | null>) {
            state.pendingImportEldType = action.payload
        },
        setEditInitialStep(state, action: PayloadAction<number | null>) {
            state.editInitialStep = action.payload
        },
        setCdlPrefillData(state, action: PayloadAction<Omit<ClientUser, '_id'> | null>) {
            state.cdlPrefillData = action.payload
        },
        /**
         * Open the add driver form with a CDL scan's prefill, or with none. One action so opening the form always decides the prefill:
         * a plain Add Driver used to reopen with the previous scan, since leaving the form does not clear it.
         */
        openAddDriverForm(state, action: PayloadAction<Omit<ClientUser, '_id'> | null>) {
            state.cdlPrefillData = action.payload
            state.currentView = 'add'
        },
    },
})

export const {
    setCurrentView,
    setSelectedDriver,
    setIsLoading,
    setSearchTerm,
    setSelectedDriverIds,
    setImportDialogOpen,
    setTerminateConfirmOpen,
    setTruckAssignModalOpen,
    setIsImportDriverLoading,
    setPendingImportEldType,
    setEditInitialStep,
    setCdlPrefillData,
    openAddDriverForm,
} = driverSlice.actions

export const selectDriverState = (state: RootState) => state.driver
export default driverSlice.reducer
